// LAT-934: the aspect-first finding card. One card = one section + one axis (backend already
// guarantees this — see runtime.ts family grouping). Primary content is the missing-aspect
// checklist: aspect name as a small header + one plain sentence. Decluttered per the LAT-934
// decisions — no "Criteria not met" block, no raw Clarify question on the card; those stayed
// diagnostic/calibration data and moved out of the user-facing surface entirely (they still drive
// the Fix-with-Claudia payload server-side, just not shown here).
//
// LAT-934 founder decision (2026-08-24, reversing the 2026-08-23 aspect-selection points 3-5
// after live-testing the preview): there is no selection UI at all. Claudia always attempts to
// fix the ENTIRE finding from the objects/evidence she has — what she can't resolve she marks
// as open in her own reply text — so a "focus on these N aspects" affordance never matched what
// actually happened server-side. Aspect rows below are DISPLAY-ONLY (name + sentence, no
// checkbox); card actions are always whole-issue, unnumbered.
import { formatQualityGuardCriticalityLabel, humanizeKey } from "@repo/shared";
import { ChevronDown, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import {
  getFindingRemediation,
  type QualityGuardCriticality,
  type QualityGuardFinding,
  qualityGuardFindingRowKey,
} from "./quality-guard-types";

// LAT-934 review (Codex P2): semantic status tokens, not raw hex — `.design-sync/conventions.md`
// reserves `destructive`/`warning`/`info` (plus `success`, used elsewhere for the all-clear state)
// as the Status token family, which maps cleanly onto the three QG severities with no invented
// color. Both light and dark values are defined for each in src/index.css, so this stays legible
// in either theme automatically — unlike a hardcoded hex, which only ever had one value.
const severityDotClassNames: Record<QualityGuardCriticality, string> = {
  critical: "bg-destructive",
  high: "bg-warning",
  recommended: "bg-info",
  optional: "bg-muted-foreground",
  info: "bg-muted-foreground",
};

type AspectRowData = { aspectId: string | null; name: string | null; sentence: string };

/**
 * One missing-aspect row: name as a small header + exactly one plain sentence. `name` is omitted
 * for the no-aspect fallback (a quality-axis finding) — the card title already names it, so a
 * second copy of the same words directly under it would be pure noise, not a second data point.
 */
function AspectRow({ name, sentence }: { name: string | null; sentence: string }) {
  return (
    <div data-testid="quality-guard-aspect-row" className="min-w-0 flex-1 space-y-0.5">
      {name ? <p className="text-xs font-medium text-foreground">{name}</p> : null}
      <p className="text-xs leading-relaxed text-muted-foreground">{sentence}</p>
    </div>
  );
}

/**
 * Builds the aspect-first checklist rows for a card.
 *
 * When the finding carries `missingAspectsJson` (completeness families), each entry becomes one
 * row: the aspect's humanized key as the header, and — because the backend does not persist a
 * per-aspect sentence, only the shared family message (see runtime.ts `buildFamilyMissingAspects`
 * / `buildFamilyFindingMessaging`) — the finding's own message/requiredFix when there is exactly
 * one missing aspect (unambiguous), or a plain generic line for each of several (a specific
 * per-aspect sentence would need a backend change; out of scope for this frontend package — see
 * PLAN.md "Known limitation").
 *
 * When there are no missing aspects (most quality-axis findings never carry them), the card falls
 * back to one implicit row built from the finding's own plain-language message/requiredFix — "an
 * empty [aspect] list reads as a checklist of what to provide" with exactly one item: the whole
 * finding.
 */
function buildAspectRows(
  missingAspects: readonly string[],
  fallbackSentence: string | null
): AspectRowData[] {
  if (missingAspects.length === 0) {
    return fallbackSentence ? [{ aspectId: null, name: null, sentence: fallbackSentence }] : [];
  }
  if (missingAspects.length === 1) {
    const aspectId = missingAspects[0] as string;
    return [
      {
        aspectId,
        name: humanizeKey(aspectId),
        sentence: fallbackSentence ?? "Still missing — not yet covered in this section.",
      },
    ];
  }
  return missingAspects.map((aspectId) => ({
    aspectId,
    name: humanizeKey(aspectId),
    sentence: "Still missing — not yet covered in this section.",
  }));
}

function RecheckingChip() {
  return (
    <span
      data-testid="quality-guard-finding-rechecking-chip"
      className="inline-flex shrink-0 items-center gap-1 bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
    >
      <Spinner className="size-3" />
      Re-checking…
    </span>
  );
}

export interface QualityGuardFindingCardProps {
  finding: QualityGuardFinding;
  /** LAT-934 point 2: pre-resolved section-state sentence — see `resolveFindingCardTitle`. */
  title: string;
  /** LAT-934 point 7: pre-resolved "Tab › Field" breadcrumb — see `resolveFindingCardLocation`. */
  location: string;
  isOpen: boolean;
  onToggleOpen: () => void;
  /** This finding's own section is mid re-check — dim the body, keep title + last-known state on screen. */
  isChecking?: boolean;
  canFix: boolean;
  fixDisabled?: boolean;
  /** Always acts on the whole finding — there is no aspect-level selection. */
  onFixWithClaudia: () => void;
  canAcceptRisk: boolean;
  /**
   * Why acceptance is closed for this finding, in the reader's own words, or `null`
   * when it is open. One sentence whatever the reason, because the control shows one
   * closed state: the server refuses these writes, and the card says so before the click.
   */
  acceptRiskRefusal: string | null;
  onAcceptRisk: () => void;
}

export function QualityGuardFindingCard({
  finding,
  title,
  location,
  isOpen,
  onToggleOpen,
  isChecking = false,
  canFix,
  fixDisabled = false,
  onFixWithClaudia,
  canAcceptRisk,
  acceptRiskRefusal,
  onAcceptRisk,
}: QualityGuardFindingCardProps) {
  // A row that names an accept key is already one issue — one element, with its own name
  // and its own sentence. The gap list it also carries belongs to the whole finding, so
  // reading it here would print the entire section's checklist on each of the sibling cards
  // beside it and leave them indistinguishable.
  const perKeyElement = typeof finding.acceptKey === "string" ? finding.title.trim() : null;
  const missingAspects = perKeyElement === null ? (finding.missingAspectsJson ?? []) : [];
  const remediation = getFindingRemediation(finding);
  // Plain-language `message` (LAT-920/LAT-825) is preferred over the more instructional
  // `requiredFix` — only used when there's exactly one item to describe (0 or 1 missing
  // aspects), since with several the shared sentence would misdescribe most of them.
  const fallbackSentence =
    (missingAspects.length <= 1
      ? (finding.message ?? remediation?.requiredFix)
      : undefined
    )?.trim() || null;
  // The element's name is dropped where the card's title already is it, the same reason
  // `AspectRow` omits the header on the no-aspect fallback.
  const perKeyName = perKeyElement && perKeyElement !== title.trim() ? perKeyElement : null;
  const aspectRows =
    perKeyElement === null
      ? buildAspectRows(missingAspects, fallbackSentence)
      : [
          {
            aspectId: finding.acceptKey ?? perKeyElement,
            name: perKeyName,
            sentence: fallbackSentence ?? "Still missing — not yet covered in this section.",
          },
        ];
  const collapsedSummary =
    perKeyElement === null
      ? missingAspects.length > 0
        ? `${missingAspects.length} aspect${missingAspects.length === 1 ? "" : "s"} missing`
        : null
      : perKeyName;

  return (
    <div
      data-testid="quality-guard-entity-finding"
      data-qg-axis={finding.axis}
      data-qg-criticality={finding.criticality}
      data-qg-impact={finding.readinessImpact}
      data-qg-finding-id={String(finding._id)}
      // What addresses this CARD. `data-qg-finding-id` names the finding, and one QG2
      // finding draws a card per open accept key, so it names every sibling at once —
      // anything that has to reach one of them, a deep link included, comes here.
      data-qg-finding-row-key={qualityGuardFindingRowKey(finding)}
      className="border-b border-border"
    >
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={onToggleOpen}
        className={cn(
          "flex w-full gap-2 px-4 py-2.5 text-left transition-colors hover:bg-accent/50",
          isOpen ? "items-start" : "items-center"
        )}
      >
        <div className="min-w-0 flex-1 space-y-0.5">
          <p
            className={cn("text-sm font-medium", isOpen ? "break-words leading-snug" : "truncate")}
          >
            {title}
          </p>
          <div className={cn("space-y-0.5", isChecking && "opacity-45")}>
            <p className="flex items-center gap-1.5 truncate text-xs text-muted-foreground">
              <span
                aria-hidden
                // rounded-full is deliberate here — a severity indicator is a status dot, one
                // of the sanctioned round shapes (.design-sync/conventions.md), same class as
                // UnreadDot/AvatarBadge.
                className={cn(
                  "inline-block size-1.5 shrink-0 rounded-full",
                  severityDotClassNames[finding.criticality]
                )}
              />
              {formatQualityGuardCriticalityLabel(finding.criticality)} · {location}
            </p>
            {!isOpen && collapsedSummary ? (
              <p className="truncate text-xs text-muted-foreground">{collapsedSummary}</p>
            ) : null}
          </div>
        </div>
        {isChecking ? (
          <RecheckingChip />
        ) : (
          <ChevronDown
            className={cn(
              "size-4 shrink-0 text-muted-foreground transition-transform",
              isOpen && "mt-0.5 rotate-180"
            )}
            aria-hidden
          />
        )}
      </button>
      {isOpen ? (
        <div
          className={cn("space-y-2.5 px-4 pb-3", isChecking && "pointer-events-none opacity-45")}
        >
          {aspectRows.length > 0 ? (
            <div data-testid="quality-guard-aspect-checklist" className="space-y-2.5">
              {aspectRows.map((row, index) => (
                <AspectRow
                  key={row.aspectId ?? row.name ?? index}
                  name={row.name}
                  sentence={row.sentence}
                />
              ))}
            </div>
          ) : null}
          {canFix || canAcceptRisk ? (
            <div className="flex flex-wrap items-center gap-2">
              {canFix ? (
                <Button
                  type="button"
                  size="sm"
                  className="h-7 gap-1.5 text-xs"
                  disabled={fixDisabled}
                  onClick={onFixWithClaudia}
                >
                  <Sparkles data-icon="inline-start" className="size-3.5" />
                  Fix with Claudia
                </Button>
              ) : null}
              {canAcceptRisk ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={acceptRiskRefusal !== null}
                  title={acceptRiskRefusal ?? undefined}
                  onClick={onAcceptRisk}
                >
                  Accept risk
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
